import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { MaskedField } from "../components/MaskedField";
import { WalletBalances } from "../components/WalletBalances";
import { WalletTotals } from "../components/WalletTotals";
import { Wallet, Pencil, Trash2, Plus, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const empty = { tok_address: "", wallet_address: "", private_key: "" };

export default function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [usd, setUsd] = useState(0);

  const load = () => api.get("/vault-wallets").then((r) => { setWallets(r.data.wallets); setUsd(r.data.usd_balance); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await api.put(`/vault-wallets/${editId}`, form);
      else await api.post("/vault-wallets", form);
      toast.success(editId ? "Wallet updated" : "Wallet encrypted & saved");
      setForm(empty);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (w) => {
    setEditId(w.id);
    setForm({ tok_address: w.tok_address, wallet_address: w.wallet_address, private_key: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const del = async (w) => {
    await api.delete(`/vault-wallets/${w.id}`);
    toast.success("Wallet deleted");
    if (editId === w.id) { setEditId(null); setForm(empty); }
    load();
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Web3 Custody</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Wallets</h1>
        <p className="mt-2 text-sm text-muted-foreground">Store TOK_ address, wallet address and private key — all encrypted. Private keys are shown masked after saving.</p>
      </div>

      <WalletTotals wallets={wallets} usd={usd} />

      <form onSubmit={save} className="rounded-xl border border-border bg-card p-6" data-testid="wallet-form">
        <h3 className="mb-4 text-lg font-medium">{editId ? "Edit wallet" : "Add wallet"}</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>TOK_ Address</Label>
            <Input value={form.tok_address} onChange={set("tok_address")} required placeholder="tok_..." data-testid="wallet-tok" />
          </div>
          <div className="space-y-1.5">
            <Label>Wallet Address</Label>
            <Input value={form.wallet_address} onChange={set("wallet_address")} required placeholder="0x..." data-testid="wallet-address" />
          </div>
          <div className="space-y-1.5">
            <Label>Private Key</Label>
            <Input value={form.private_key} onChange={set("private_key")} required placeholder={editId ? "Enter new key" : "0x..."} data-testid="wallet-pk" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-primary" /> All three values are encrypted server-side. The raw private key is never exposed again.
        </div>
        <div className="mt-4 flex gap-3">
          <Button type="submit" disabled={saving} data-testid="wallet-save">
            {editId ? <Pencil className="mr-1.5 h-4 w-4" /> : <Plus className="mr-1.5 h-4 w-4" />}
            {saving ? "Saving…" : editId ? "Update wallet" : "Save wallet"}
          </Button>
          {editId && <Button type="button" variant="ghost" onClick={() => { setEditId(null); setForm(empty); }}>Cancel</Button>}
        </div>
      </form>

      <div className="space-y-3">
        <h3 className="text-lg font-medium">Saved wallets ({wallets.length})</h3>
        {wallets.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card py-12 text-center text-sm text-muted-foreground">
            <Wallet className="h-8 w-8 opacity-40" /> No wallets saved yet.
          </div>
        ) : wallets.map((w) => (
          <div key={w.id} className="rounded-xl border border-border bg-card p-5" data-testid={`wallet-row-${w.id}`}>
            <div className="grid gap-3 sm:grid-cols-3">
              <MaskedField label="TOK_ Address" value={w.tok_address} copyValue={w.tok_address} testid={`wallet-tok-${w.id}`} />
              <MaskedField label="Wallet Address" value={w.wallet_address} copyValue={w.wallet_address} testid={`wallet-addr-${w.id}`} />
              <MaskedField label="Private Key" value={w.pk_mask} testid={`wallet-pk-${w.id}`} />
            </div>
            <WalletBalances wallet={w} usd={usd} onSaved={load} />
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => edit(w)} data-testid={`wallet-edit-${w.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => del(w)} data-testid={`wallet-del-${w.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
